// src/utils/expenseReceiptMatcher.js
// Auto-links statement-imported expenses to uploaded reimbursement receipts.
// Scoring: amount match +50, same-day +30, within-3-days +15, vendor overlap +20.
// Auto-link threshold: >= 65 points.

const AUTO_LINK_THRESHOLD = 65;

/**
 * Normalize a string for fuzzy comparison (lowercase, collapse whitespace, strip punctuation).
 */
function normalize(str) {
  return (str || '').toLowerCase().replace(/[^a-z0-9\s]/g, '').replace(/\s+/g, ' ').trim();
}

/**
 * Calculate day difference between two YYYY-MM-DD date strings.
 */
function daysBetween(dateA, dateB) {
  if (!dateA || !dateB) return Infinity;
  const a = new Date(dateA);
  const b = new Date(dateB);
  return Math.abs(Math.round((a - b) / 86400000));
}

/**
 * Check if two strings share meaningful tokens.
 */
function hasOverlap(a, b) {
  const tokensA = normalize(a).split(' ').filter(t => t.length > 2);
  const tokensB = new Set(normalize(b).split(' ').filter(t => t.length > 2));
  return tokensA.some(t => tokensB.has(t));
}

/**
 * Score how well a reimbursement matches an expense.
 * @param {object} expense - { amount, amountInInr, date, description, category, currencyOriginal }
 * @param {object} reimbursement - { amount, amountInInr, expenseDate, vendor, description, currency }
 * @returns {number} match score (0-100)
 */
export function scoreMatch(expense, reimbursement) {
  let score = 0;

  // Amount match — compare in same currency where possible
  const expAmt = Number(expense.amountInInr || expense.amount || 0);
  const reimbAmt = Number(reimbursement.amountInInr || reimbursement.amount || 0);
  if (expAmt > 0 && reimbAmt > 0 && Math.abs(expAmt - reimbAmt) <= 0.01 * Math.max(expAmt, reimbAmt)) {
    score += 50;
  }

  // Date match
  const expDate = expense.date || expense.expenseDate;
  const reimbDate = reimbursement.expenseDate;
  const days = daysBetween(expDate, reimbDate);
  if (days === 0) score += 30;
  else if (days <= 3) score += 15;

  // Vendor/description overlap
  const expText = `${expense.description || ''} ${expense.category || ''}`;
  const reimbText = `${reimbursement.vendor || ''} ${reimbursement.description || ''}`;
  if (hasOverlap(expText, reimbText)) score += 20;

  return score;
}

/**
 * Find the best matching reimbursement for an expense.
 * @param {object} expense - expense from statement import
 * @param {Array} reimbursements - all available reimbursements
 * @returns {{ reimbursement: object|null, score: number }}
 */
export function findBestMatch(expense, reimbursements) {
  let best = null;
  let bestScore = 0;

  for (const r of reimbursements) {
    // Skip already-linked reimbursements
    if (r.linkedExpenseId) continue;

    const s = scoreMatch(expense, r);
    if (s > bestScore) {
      bestScore = s;
      best = r;
    }
  }

  return { reimbursement: best, score: bestScore };
}

/**
 * Auto-link a batch of expenses to reimbursements.
 * Returns array of { expenseId, reimbursementId, score } for successful links.
 * Only links when score >= AUTO_LINK_THRESHOLD.
 *
 * @param {Array} expenses - imported expenses (must have .id)
 * @param {Array} reimbursements - all reimbursements
 * @returns {Array<{ expenseId: number, reimbursementId: number, score: number }>}
 */
export function autoLinkBatch(expenses, reimbursements) {
  const links = [];
  // Track which reimbursements are already claimed in this batch
  const claimedReimbursements = new Set();

  for (const exp of expenses) {
    const available = reimbursements.filter(r => !claimedReimbursements.has(r.id));
    const { reimbursement, score } = findBestMatch(exp, available);

    if (reimbursement && score >= AUTO_LINK_THRESHOLD) {
      links.push({
        expenseId: exp.id,
        reimbursementId: reimbursement.id,
        score,
      });
      claimedReimbursements.add(reimbursement.id);
    }
  }

  return links;
}
